import { useEffect, useState } from "react";

import { useNavigate, useSearchParams } from "react-router-dom";

import { fetchKakaoToken, spotOnLogin } from "@/apis/login";
import {
  fetchCommunityOptions,
  fetchGarretOptions,
  fetchLeafOptions,
} from "@/apis/organization";
import { getUserInfo } from "@/apis/user";
import {
  DASHBOARD_URL,
  HOME_MAIN_URL,
  LOGIN_MAIN_URL,
  LOGIN_QR_URL,
  LOGIN_SIGNUP_URL,
} from "@/constants/routes";
import LoginLayout from "@/pages/Login/components/LoginLayout";
import useLoginStore from "@/store/loginStore";
import useToastStore from "@/store/toastStore";
import useUserStore from "@/store/userStore";
import { consumePostLoginRedirect } from "@/utils/postLoginRedirect";

// /kakao/auth
const KakaoLogin = () => {
  const { kakaoToken, saveKakaoToken, saveSpotOnToken, saveTokenId } =
    useLoginStore();
  const { saveUserInfo } = useUserStore();
  const showToast = useToastStore(s => s.showToast);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [accessCode, setAccessCode] = useState("");
  useEffect(() => {
    getAccessCode();
  }, []);

  useEffect(() => {
    if (accessCode.length > 0) {
      getKakaoToken(accessCode);
    }
  }, [accessCode]);

  useEffect(() => {
    if (kakaoToken.length > 0) {
      getSpotOnToken();
    }
  }, [kakaoToken]);

  const getAccessCode = () => {
    const code = searchParams.get("code");
    if (code && code.length > 0) {
      setAccessCode(code);
    }
  };

  /* Access Code로 Access Token 받아오는 Kakao  API */
  const getKakaoToken = async (code: string) => {
    const token = await fetchKakaoToken(code);

    if (token) {
      saveKakaoToken(token);
    } else {
      // 인증 코드 재사용·만료, 카카오 장애 등 — 스피너에 갇히지 않게 로그인 화면으로
      showToast("카카오 로그인에 실패했습니다. 다시 시도해주세요.");
      navigate(LOGIN_MAIN_URL, { replace: true });
    }
  };

  /** 카카오 로그인이 된 이후에 spotOn 로그인 시도 */
  const getSpotOnToken = async () => {
    const { status, token, tokenId, userStateCode } =
      await spotOnLogin(kakaoToken);

    // 회원가입이 안된 경우
    if (status === 412) {
      navigate(LOGIN_SIGNUP_URL, { state: kakaoToken });
      return;
    }

    // 회원가입은 됐지만 아직 승인 전 → 승인 대기 화면 (JWT 는 저장하지 않는다)
    if (status === 200 && userStateCode === "00") {
      navigate(LOGIN_QR_URL, { replace: true });
      return;
    }

    // 회원가입이 된 경우 && 승인이 된 경우
    if (status === 200) {
      // JWT 토큰을 먼저 저장해야 이후 API 호출에서 인증 통과
      saveSpotOnToken(token);
      saveTokenId(tokenId);

      const info = await getUserInfo(tokenId);

      const { cmtCd, garCd, leafCd } = info;

      const cmtNm = (await fetchCommunityOptions()).find(
        option => option.id === cmtCd,
      )?.name;
      const garNm = (await fetchGarretOptions(cmtCd)).find(
        option => option.id === garCd,
      )?.name;
      const leafNm = (await fetchLeafOptions(cmtCd, garCd)).find(
        option => option.id === leafCd,
      )?.name;
      const userInfo = {
        ...info,
        cmtNm,
        garNm,
        leafNm,
      };

      saveUserInfo(userInfo);
      const isAdminUser = info.roleId === "ADMIN" || info.roleId === "MINISTRY";
      navigate(
        consumePostLoginRedirect() ??
          (isAdminUser ? DASHBOARD_URL : HOME_MAIN_URL),
      );
      return;
    }

    // 그 외 실패. 401(탈퇴 계정 등)·네트워크 오류·5xx 는 인터셉터가 이미 처리한다.
    if (status && status !== 401 && status < 500) {
      showToast("로그인에 실패했습니다. 잠시 후 다시 시도해주세요.");
    }
    navigate(LOGIN_MAIN_URL, { replace: true });
  };

  return (
    <LoginLayout>
      <div className="fixed left-0 top-0 z-40 flex h-full w-full items-center justify-center bg-white opacity-60">
        <div role="status" className="flex w-12 flex-row justify-center">
          <svg
            aria-hidden="true"
            className="h-24 w-24 animate-spin fill-blue-600 text-gray-200 dark:text-gray-600"
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"
            />
          </svg>
          <span className="sr-only">Loading...</span>
        </div>
      </div>

      <div className="relative flex flex-col items-center justify-center">
        <p className="text-[13px] text-white/50">로그인 중...</p>
      </div>
    </LoginLayout>
  );
};

export default KakaoLogin;
